import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChartBar,
    faChartLine,
    faChartPie,
    faCheckCircle,
    faClock,
    faDollarSign,
    faHandshake,
    faRefresh,
    faUsers,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import { useBaseUrl } from "../../../utils/utils";
import ServiceCenterShell from "./ServiceCenterShell";

interface AnalyticsData {
    totalRequests: number;
    pendingRequests: number;
    completedRequests: number;
    rejectedRequests: number;
    totalApplications: number;
    acceptedApplications: number;
    averageResponseTime: number;
    totalRevenue: number;
    monthlyTrends: {
        month: string;
        requests: number;
        applications: number;
        revenue: number;
    }[];
    statusDistribution: {
        status: string;
        count: number;
        percentage: number;
    }[];
    topServices: {
        service: string;
        requests: number;
        applications: number;
        revenue: number;
    }[];
    partnerPerformance: {
        partner: string;
        applications: number;
        acceptanceRate: number;
        averageRating: number;
    }[];
    trends: {
        requestsChange: number;
        applicationsChange: number;
        revenueChange: number;
    };
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 0,
    }).format(amount || 0);
};

const AnalyticsPage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "1y">("30d");
    const [viewMode, setViewMode] = useState<"overview" | "detailed" | "comparison">("overview");

    useEffect(() => {
        document.title = "Service Analytics - Uzima Healthcare";
        fetchAnalyticsData();
    }, [timeRange, baseUrl]);

    const fetchAnalyticsData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${baseUrl}collaborations/analytics/?time_range=${timeRange}`);
            if (!response.ok) {
                setAnalyticsData(null);
                return;
            }
            const data = await response.json();
            setAnalyticsData(data.status === "success" && data.data ? data.data : null);
        } catch (error) {
            console.error("Error fetching analytics:", error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const statusColor = (status: string) => {
        const normalized = status.toLowerCase();
        if (normalized.includes("pending")) return "#f59e0b";
        if (normalized.includes("completed") || normalized.includes("agreed")) return "#10b981";
        if (normalized.includes("rejected")) return "#f43f5e";
        return "#3b82f6";
    };

    const trendLabel = (value: number) => `${value > 0 ? "+" : ""}${value}% vs previous period`;

    const topPartner = useMemo(() => {
        return [...(analyticsData?.partnerPerformance || [])].sort((a, b) => b.averageRating - a.averageRating)[0];
    }, [analyticsData]);

    return (
        <ServiceCenterShell
            title="Analytics"
            description="Track service request performance, application movement, revenue trends, and partner responsiveness in one aligned view."
            activeTab="analytics"
            action={
                <button className="sc-btn sc-btn-outline" onClick={fetchAnalyticsData}>
                    <FontAwesomeIcon icon={faRefresh} /> Refresh
                </button>
            }
        >
            <div className="sc-toolbar">
                <select className="sc-select" value={timeRange} onChange={(event) => setTimeRange(event.target.value as "7d" | "30d" | "90d" | "1y")}>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="1y">Last 12 months</option>
                </select>
                <select className="sc-select" value={viewMode} onChange={(event) => setViewMode(event.target.value as "overview" | "detailed" | "comparison") }>
                    <option value="overview">Overview</option>
                    <option value="detailed">Detailed</option>
                    <option value="comparison">Comparison</option>
                </select>
                <span className="sc-chip soft">Time range updates analytics in real time</span>
            </div>

            {loading ? (
                <div className="sc-spinner"></div>
            ) : !analyticsData ? (
                <div className="sc-empty">
                    <i className="bx bx-bar-chart-alt-2"></i>
                    <h3>No analytics data available</h3>
                    <p>Analytics will appear here once your service center has requests, applications, and activity to measure.</p>
                    <button className="sc-btn sc-btn-primary" onClick={fetchAnalyticsData}><FontAwesomeIcon icon={faRefresh} /> Reload analytics</button>
                </div>
            ) : (
                <>
                    <div className="sc-stats">
                        <div className="sc-stat">
                            <div className="sc-stat-icon teal"><FontAwesomeIcon icon={faEye} /></div>
                            <div className="sc-stat-value">{analyticsData.totalRequests}</div>
                            <div className="sc-stat-label">Total Requests</div>
                            <div className={`sc-stat-trend ${analyticsData.trends.requestsChange >= 0 ? "up" : "down"}`}>{trendLabel(analyticsData.trends.requestsChange)}</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon blue"><FontAwesomeIcon icon={faHandshake} /></div>
                            <div className="sc-stat-value">{analyticsData.totalApplications}</div>
                            <div className="sc-stat-label">Applications</div>
                            <div className={`sc-stat-trend ${analyticsData.trends.applicationsChange >= 0 ? "up" : "down"}`}>{trendLabel(analyticsData.trends.applicationsChange)}</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon amber"><FontAwesomeIcon icon={faClock} /></div>
                            <div className="sc-stat-value">{analyticsData.averageResponseTime}h</div>
                            <div className="sc-stat-label">Average Response</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon violet"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <div className="sc-stat-value">{formatCurrency(analyticsData.totalRevenue)}</div>
                            <div className="sc-stat-label">Revenue</div>
                            <div className={`sc-stat-trend ${analyticsData.trends.revenueChange >= 0 ? "up" : "down"}`}>{trendLabel(analyticsData.trends.revenueChange)}</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon rose"><FontAwesomeIcon icon={faCheckCircle} /></div>
                            <div className="sc-stat-value">{analyticsData.acceptedApplications}</div>
                            <div className="sc-stat-label">Accepted Applications</div>
                        </div>
                    </div>

                    {viewMode === "overview" && (
                        <div className="sc-split">
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>Status distribution</h3>
                                        <p>See how requests are currently spread across workflow states.</p>
                                    </div>
                                </div>
                                <div className="sc-panel-body">
                                    <div className="sc-list-stack">
                                        {analyticsData.statusDistribution.map((item, index) => (
                                            <div className="sc-list-item" key={`${item.status}-${index}`}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                                                        <strong>{item.status}</strong>
                                                        <span>{item.count} requests</span>
                                                    </div>
                                                    <div className="sc-meter"><div style={{ width: `${Math.max(6, item.percentage)}%`, background: statusColor(item.status) }}></div></div>
                                                </div>
                                                <strong style={{ minWidth: 52, textAlign: "right" }}>{item.percentage}%</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>Top services</h3>
                                        <p>Best-performing services by demand and revenue.</p>
                                    </div>
                                </div>
                                <div className="sc-panel-body">
                                    <div className="sc-list-stack">
                                        {analyticsData.topServices.slice(0, 5).map((service, index) => (
                                            <div className="sc-list-item" key={`${service.service}-${index}`}>
                                                <div>
                                                    <strong>{service.service}</strong>
                                                    <small style={{ display: "block", marginTop: 4 }}>{service.requests} requests · {service.applications} applications</small>
                                                </div>
                                                <strong>{formatCurrency(service.revenue)}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {viewMode === "detailed" && (
                        <div className="sc-split">
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>Partner performance</h3>
                                        <p>How partners are performing on applications and acceptance quality.</p>
                                    </div>
                                </div>
                                <div className="sc-panel-body">
                                    <div className="sc-table-wrap">
                                        <table className="sc-table">
                                            <thead>
                                                <tr>
                                                    <th>Partner</th>
                                                    <th>Applications</th>
                                                    <th>Acceptance</th>
                                                    <th>Rating</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analyticsData.partnerPerformance.map((partner, index) => (
                                                    <tr key={`${partner.partner}-${index}`}>
                                                        <td><strong>{partner.partner}</strong></td>
                                                        <td>{partner.applications}</td>
                                                        <td>{partner.acceptanceRate}%</td>
                                                        <td>{partner.averageRating}/5.0</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>Detailed highlights</h3>
                                        <p>Quick readouts for operational review.</p>
                                    </div>
                                </div>
                                <div className="sc-panel-body">
                                    <div className="sc-kpi-grid">
                                        <div className="sc-kpi-card">
                                            <h4>Pending requests</h4>
                                            <strong>{analyticsData.pendingRequests}</strong>
                                            <span>awaiting next action</span>
                                        </div>
                                        <div className="sc-kpi-card">
                                            <h4>Completed requests</h4>
                                            <strong>{analyticsData.completedRequests}</strong>
                                            <span>closed successfully</span>
                                        </div>
                                        <div className="sc-kpi-card">
                                            <h4>Rejected requests</h4>
                                            <strong>{analyticsData.rejectedRequests}</strong>
                                            <span>rejected or not progressed</span>
                                        </div>
                                        <div className="sc-kpi-card">
                                            <h4>Top-rated partner</h4>
                                            <strong>{topPartner?.partner || "—"}</strong>
                                            <span>{topPartner ? `${topPartner.averageRating}/5.0 average rating` : "No partner analytics yet"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {viewMode === "comparison" && (
                        <div className="sc-panel">
                            <div className="sc-panel-head">
                                <div>
                                    <h3>Monthly trends comparison</h3>
                                    <p>Compare request, application, and revenue movement over time.</p>
                                </div>
                            </div>
                            <div className="sc-panel-body">
                                <div className="sc-table-wrap">
                                    <table className="sc-table">
                                        <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>Requests</th>
                                                <th>Applications</th>
                                                <th>Revenue</th>
                                                <th>Growth</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analyticsData.monthlyTrends.map((month, index) => {
                                                const previous = index > 0 ? analyticsData.monthlyTrends[index - 1] : null;
                                                const growth = previous && previous.requests ? ((month.requests - previous.requests) / previous.requests) * 100 : 0;
                                                return (
                                                    <tr key={`${month.month}-${index}`}>
                                                        <td><strong>{month.month}</strong></td>
                                                        <td>{month.requests}</td>
                                                        <td>{month.applications}</td>
                                                        <td>{formatCurrency(month.revenue)}</td>
                                                        <td style={{ color: growth >= 0 ? "#059669" : "#f43f5e", fontWeight: 700 }}>{growth >= 0 ? "+" : ""}{growth.toFixed(1)}%</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="sc-panel">
                        <div className="sc-panel-head">
                            <div>
                                <h3>Operational takeaways</h3>
                                <p>Use this to guide rate reviews, partner follow-ups, and workflow refinement.</p>
                            </div>
                        </div>
                        <div className="sc-panel-body">
                            <div className="sc-kpi-grid">
                                <div className="sc-kpi-card">
                                    <h4>Response health</h4>
                                    <strong>{analyticsData.averageResponseTime}h</strong>
                                    <span>average response turnaround</span>
                                </div>
                                <div className="sc-kpi-card">
                                    <h4>Conversion quality</h4>
                                    <strong>{analyticsData.totalApplications ? Math.round((analyticsData.acceptedApplications / analyticsData.totalApplications) * 100) : 0}%</strong>
                                    <span>accepted application ratio</span>
                                </div>
                                <div className="sc-kpi-card">
                                    <h4>Partner pool</h4>
                                    <strong>{analyticsData.partnerPerformance.length}</strong>
                                    <span>partners represented in analytics</span>
                                </div>
                                <div className="sc-kpi-card">
                                    <h4>Revenue per request</h4>
                                    <strong>{formatCurrency(analyticsData.totalRequests ? analyticsData.totalRevenue / analyticsData.totalRequests : 0)}</strong>
                                    <span>average revenue generation</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ServiceCenterShell>
    );
};

export default AnalyticsPage;
